import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import { getOriginalBlockId, isRuntimeBlockId } from '../../../utils/runTimeBlocks';
import type { GlobalStateStoreInstance } from '../../GlobalStateStore/GlobalStateStoreManager';
import type { InstanceService } from '../types';

function isDraggableCard(block: BlockStateUnionType | undefined): boolean {
  if (!block || block.componentType !== 'Card') {
    return false;
  }
  return Boolean(block.content.addOns?.draggable?.data);
}

function collectDraggableBlockIds(
  blocks: Record<string, BlockStateUnionType | undefined>,
): string[] {
  const draggableIds: string[] = [];

  for (const blockId of Object.keys(blocks)) {
    const block = blocks[blockId];
    if (!block) continue;

    if (isDraggableCard(block)) {
      draggableIds.push(blockId);
      continue;
    }

    // for runtime blocks, check if their original block is a draggable card
    if (isRuntimeBlockId(blockId)) {
      const originalBlockId = getOriginalBlockId(blockId);
      const originalBlock = blocks[originalBlockId];

      if (isDraggableCard(originalBlock)) {
        draggableIds.push(blockId);
      }
    }
  }

  return draggableIds;
}

function shallowEqualStringArray(left: string[], right: string[]): boolean {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index++) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

/**
 * Core subscription: derive `draggableBlockIds` from `blocks` and push it
 * back into the same gss store. Works against any gss store — base or
 * instance-scoped. Consumers own the signal/lifecycle.
 *
 *  - emits an initial value synchronously (mirrors the legacy hook's
 *    mount-time useEffect),
 *  - subscribes to the store; recomputes only when the `blocks` ref differs
 *    and writes only when the id list actually changes (avoids redundant
 *    zustand notifications),
 *  - returns a disposer. Aborted-signal guard prevents a write if the
 *    owner is torn down mid-listener.
 */
export function subscribeDraggableBlocks(
  store: GlobalStateStoreInstance,
  signal: AbortSignal,
): () => void {
  let blocks = store.getState().blocks;
  let lastIds: string[] = [];
  const firstBlockIds: string[] = collectDraggableBlockIds(blocks);

  const writeIfChanged = (nextIds: string[]) => {
    if (signal.aborted) return;
    if (shallowEqualStringArray(lastIds, nextIds)) return;
    lastIds = nextIds;
    store.getState().actions.setDraggableBlockIds(nextIds);
  };

  // initial emit — matches the hook's first-render useEffect behavior
  writeIfChanged(firstBlockIds);

  return store.subscribe((state) => {
    if (state.blocks === blocks) return;
    blocks = state.blocks;
    writeIfChanged(collectDraggableBlockIds(blocks));
  });
}

/**
 * Per-instance service wrapper around `subscribeDraggableBlocks`.
 * Registered in `INSTANCE_SERVICES`; the base page wires the core
 * subscription directly via `BaseGlobalStateStoreProvider`.
 */
export const DraggableBlocksService: InstanceService = (ctx) => {
  return subscribeDraggableBlocks(ctx.stores.gss, ctx.signal);
};
